import { useEffect, useState, useContext } from "react";
import { ScheduleContext } from "context/schedule";
import { useMaterialUIController } from "context";

import PropTypes from "prop-types";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import GlobalStyles from "@mui/material/GlobalStyles";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import Swal from "sweetalert2";

import EventService from "services/event.service";
import ClientService from "services/client.service";


export default function CreateEventModal({ open, onClose, event, selectedDate, selectedEvent }) {
  const isEdit = Boolean(event);
  const { loadEvents } = useContext(ScheduleContext);
  const [clients, setClients] = useState([]);

  const [controller] = useMaterialUIController();
  const { darkMode, sidenavColor } = controller;

  const inputStyle = {
    "& .MuiInputBase-input": { color: darkMode ? "#fff" : "inherit" },
    "& .MuiInputLabel-root": { color: darkMode ? "#fff" : "inherit" },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: darkMode ? "rgba(255,255,255,0.3)" : "inherit" },
    "& .MuiSvgIcon-root": { color: darkMode ? "#fff" : "inherit" },
  };

  const [loading, setLoading] = useState(false);
  
  // Obter data de hoje para validação
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };
  
  const [todayDate] = useState(getTodayDate());
  const [dateError, setDateError] = useState("");

  const [form, setForm] = useState({
    title: "",
    event_type: "aniversario",
    event_date: "",
    start_time: "",
    end_time: "",
    location: "",
    city: "",
    price: "",
    status: "draft",
  });

  const initialState = {
    title: "",
    event_date: "",
    start_time: "",
    end_time: "",
    location: "",
    city: "",
    price: "",
    event_type: "aniversario",
    status: "draft",
    client_id: null,
  };


  // 🔁 Preenche formulário quando editar
  useEffect(() => {
    if (event) {
      setForm({
        title: event.title || "",
        event_type: event.event_type || "aniversario",
        event_date: event.event_date
          ? event.event_date.split("T")[0]
          : "",
        start_time: event.start_time || "",
        end_time: event.end_time || "",
        location: event.location || "",
        city: event.city || "",
        price: event.price || "",
        status: event.status || "draft",
        client_id: event.client_id || null,
      });
    } else {
      setForm(initialState)
    }
  }, [event, open]);

  useEffect(() => {
    if (selectedDate && open) {
      const [date, time] = selectedDate.split('T');
      setForm((prev) => ({
        ...prev,
        event_date: date,
        start_time: time ? time.substring(0, 5) : prev.start_time,
      }));
    }
  }, [selectedDate, open]);

  useEffect(() => {
    if (open) {
      ClientService.getClients()
        .then(setClients)
        .catch(() => {
          console.error("Erro ao carregar clientes");
        });
    }
  }, [open]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    
    // Validar data se o campo mudado for a data
    if (name === "event_date") {
      if (value < todayDate && !isEdit) {
        setDateError("A data não pode ser no passado");
      } else {
        setDateError("");
      }
    }
  };

  const handleSubmit = async () => {
    try {
      // Validar data se for novo evento
      if (!isEdit && form.event_date < todayDate) {
        Swal.fire({
          icon: "error",
          title: "Data Inválida",
          text: "A data do evento não pode ser no passado",
        });
        return;
      }

      setLoading(true);

      if (isEdit) {
        await EventService.updateEvent(event.id, form);
        onClose();
        await Swal.fire({
          icon: "success",
          title: "Evento atualizado!",
          timer: 1200,
          showConfirmButton: false,
        });
      } else {
        await EventService.createEvent(form);
        onClose();
            setForm(initialState);
        await Swal.fire({
          icon: "success",
          title: "Evento criado",
          timer: 1200,
          showConfirmButton: false,
        });
      }
      await loadEvents();
    } catch (error) {
      onClose();
      console.error("Erro ao salvar evento:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Erro ao salvar evento",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEvent = async () => {

    if (!selectedEvent || !selectedEvent.id) {
      console.error("Evento não definido para exclusão");
      return;
    }

    onClose();

    const result = await Swal.fire({
      title: "Excluir evento?",
      text: "Essa ação não pode ser desfeita.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonText: "Cancelar",
      confirmButtonText: "Sim, excluir",
    });

    if (!result.isConfirmed) return;

    try {
      await EventService.cancelEvent(selectedEvent.id);

      await Swal.fire({
        icon: "success",
        title: "Evento excluído",
        timer: 1200,
        showConfirmButton: false,
      });


      loadEvents();
    } catch (error) {
      console.error(error);

      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível excluir o evento",
      });
    }
  };

  const handleRestoreEvent = async () => {
    if (!event || !event.id) return;

    const result = await Swal.fire({
      title: "Reativar evento?",
      text: "O evento voltará como Rascunho para você revisar.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#1A73E8",
      cancelButtonText: "Cancelar",
      confirmButtonText: "Sim, reativar",
    });

    if (!result.isConfirmed) return;

    try {
      await EventService.restoreEvent(event.id);

      await Swal.fire({
        icon: "success",
        title: "Evento reativado!",
        text: "Verifique os dados antes de confirmar novamente.",
        timer: 2000,
        showConfirmButton: false,
      });

      onClose();
      loadEvents();
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível reativar o evento",
      });
    }
  };


  return (
    <>
    <GlobalStyles styles={{ ".swal2-container": { zIndex: "99999 !important" } }} />
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="sm"
      PaperProps={{
        style: {
            backgroundColor: darkMode ? "#202940" : "#fff",
            color: darkMode ? "#fff" : "inherit",
        },
      }}
    >
      <DialogTitle sx={{ color: darkMode ? "#fff" : "inherit" }}>
        {isEdit ? "Editar Evento" : "Novo Evento"}
      </DialogTitle>

      <DialogContent>
        <MDBox display="flex" flexDirection="column" gap={2} mt={1}>
          <MDInput
            label="Título do Evento"
            name="title"
            value={form.title}
            onChange={handleChange}
            fullWidth
            sx={inputStyle}
          />

          <MDInput
            select
            label="Tipo de Evento"
            name="event_type"
            value={form.event_type}
            onChange={handleChange}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={inputStyle}
          >
            <option style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#202940" : "#fff" }} value="aniversario">Aniversário</option>
            <option style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#202940" : "#fff" }} value="casamento">Casamento</option>
            <option style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#202940" : "#fff" }} value="debutante">Debutante</option>
            <option style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#202940" : "#fff" }} value="formatura">Formatura</option>
            <option style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#202940" : "#fff" }} value="residente">Residente</option>
            <option style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#202940" : "#fff" }} value="corporativo">Corporativo</option>
          </MDInput>

          <MDInput
            type="date"
            label="Data"
            name="event_date"
            value={form.event_date}
            onChange={(e) => {
              setForm({ ...form, event_date: e.target.value });
              // Validar data
              if (e.target.value < todayDate && !isEdit) {
                setDateError("A data não pode ser no passado");
              } else {
                setDateError("");
              }
            }}
            fullWidth
            inputProps={{
              min: isEdit ? undefined : todayDate // Bloquear datas passadas no input
            }}
            InputLabelProps={{ shrink: true }}
            error={Boolean(dateError)}
            helperText={dateError}
            sx={inputStyle}
          />

          <MDBox display="flex" gap={2}>
            <MDInput
              type="time"
              label="Hora Inicial"
              name="start_time"
              value={form.start_time}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={inputStyle}
            />
            <MDInput
              type="time"
              label="Hora Final"
              name="end_time"
              value={form.end_time}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={inputStyle}
            />
          </MDBox>

          <MDInput
            label="Local (Nome do espaço)"
            name="location"
            value={form.location}
            onChange={handleChange}
            fullWidth
            sx={inputStyle}
          />

          <MDInput
            label="Cidade"
            name="city"
            value={form.city}
            onChange={handleChange}
            fullWidth
            sx={inputStyle}
          />

          <MDInput
            label="Valor"
            name="price"
            type="number"
            value={form.price}
            onChange={handleChange}
            fullWidth
            sx={inputStyle}
          />


          <MDInput
            select
            label="Cliente"
            name="client_id"
            value={form.client_id || ""}
            onChange={(e) =>
              setForm({
                ...form,
                client_id: e.target.value ? Number(e.target.value) : null,
              })
            }
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}

            fullWidth
            sx={inputStyle}
          >
            <option style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#202940" : "#fff" }} value="">Sem cliente</option>

            {clients.map((client) => (
              <option style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#202940" : "#fff" }} key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </MDInput>

          <MDInput
            select
            label="Status"
            name="status"
            value={form.status}
            onChange={handleChange}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}

            fullWidth
            sx={inputStyle}
          >
            <option style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#202940" : "#fff" }} value="draft">Rascunho</option>
            <option style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#202940" : "#fff" }} value="confirmed">Confirmado</option>
            <option style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#202940" : "#fff" }} value="completed">Concluído</option>
            <option style={{ color: darkMode ? "#fff" : "#000", backgroundColor: darkMode ? "#202940" : "#fff" }} value="cancelled">Cancelado</option>
          </MDInput>
        </MDBox>
      </DialogContent>

      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose}>
          Cancelar
        </MDButton>

        <MDButton
          variant="gradient"
          color={sidenavColor}
          onClick={handleSubmit}
          disabled={loading || (dateError !== "")}
        >
          {loading ? "Processando..." : (isEdit ? "Atualizar Evento" : "Criar Evento")}
        </MDButton>

        {isEdit && form.status === 'cancelled' ? (
          <MDButton
            variant="gradient"
            color="success"
            onClick={handleRestoreEvent}
          >
            Reativar Evento
          </MDButton>
        ) : isEdit && (
          <MDButton
            variant="outlined"
            color="error"
            onClick={handleCancelEvent}
          >
            Excluir Evento
          </MDButton>
        )}

      </DialogActions>
    </Dialog>
    </>
  );
}

CreateEventModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  event: PropTypes.object,
};
